<template>
    <div class="mt-10 rounded-md bg-white p-6 shadow-md">
        <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
            <div class="flex flex-col sm:flex-row sm:items-center">
                <div
                    class="h-20 w-20 shrink-0 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                >
                    <img
                        :src="
                            selectedUser.profile_photo_path
                                ? selectedUser.profile_photo_path
                                : selectedUser.profile_photo_url
                        "
                        class="h-20 w-20 rounded-full bg-white object-cover"
                    />
                </div>
                <div class="mt-2 flex flex-col sm:ml-10 sm:mt-0">
                    <h2 class="text-2xl font-bold text-gray-800">
                        {{ selectedUser.name }}
                    </h2>
                    <div class="text-xl font-medium text-gray-500">
                        {{ selectedUser.email }}
                    </div>
                </div>
            </div>

            <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
            >
                <div class="ml-8 flex">
                    <AtomicButton
                        v-tooltip="{
                            content:
                                selectedUser.email_verified_at === null
                                    ? 'You can impersonate user only if email is verified.'
                                    : '',
                            theme: 'info-tooltip',
                        }"
                        size="sm"
                        color="purple"
                        :disabled="selectedUser.email_verified_at === null"
                        @click="handleImpersonate(selectedUser)"
                    >
                        <template #icon>
                            <EyeIcon class="mr-2 h-6 w-6" />
                        </template>
                        Impersonate
                    </AtomicButton>
                </div>

                <div class="ml-2">
                    <AtomicButton
                        size="sm"
                        color="yellow"
                        class="mt-5 sm:mt-0"
                        @click="handleResetPassword(selectedUser)"
                    >
                        <template #icon>
                            <AtSymbolIcon class="mr-2 h-6 w-6" />
                        </template>
                        Reset password
                    </AtomicButton>
                </div>

                <div v-if="!personalTeam.new_flexibee_id" class="ml-2">
                    <AtomicButton
                        size="sm"
                        color="yellow"
                        class="mt-5 sm:mt-0"
                        @click="createFlexibeeId()"
                    >
                        <template #icon>
                            <DocumentTextIcon class="mr-2 h-6 w-6" />
                        </template>
                        Create Flexibee ID
                    </AtomicButton>
                </div>

                <div class="ml-2">
                    <AtomicButton
                        size="lg"
                        color="red"
                        class="mt-5 sm:mt-0"
                        @click="handleDeleteUser(selectedUser)"
                    >
                        <template #icon>
                            <TrashIcon class="mr-2 h-6 w-6" />
                        </template>
                        Delete user
                    </AtomicButton>
                </div>
            </div>
        </div>

        <div class="flex justify-end gap-2">
            <AtomicButton
                v-if="
                    selectedUser.pro_subscription ||
                    selectedUser.plugin_subscription
                "
                color="yellow"
                size="sm"
                @click="showManageRecurringPaymentRequestsModal = true"
            >
                Manage recurring payment requests
            </AtomicButton>

            <AtomicButton
                color="yellow"
                size="sm"
                @click="showPaymentRequestModal = true"
            >
                <template #icon>
                    <BanknotesIcon class="mr-2 h-6 w-6" />
                </template>

                Payment request
            </AtomicButton>

            <AtomicButton
                v-if="personalTeam.bucket_blocked_at === null"
                size="sm"
                color="red"
                class="mt-5 sm:mt-0"
                @click="makeBucketPrivate(selectedUser)"
            >
                <template #icon>
                    <FireIcon class="mr-2 h-6 w-6" />
                </template>
                Block storage
            </AtomicButton>

            <AtomicButton
                v-if="personalTeam.bucket_blocked_at !== null"
                color="yellow"
                size="sm"
                @click="makeBucketPublic(selectedUser)"
            >
                <template #icon>
                    <SparklesIcon class="mr-2 h-6 w-6" />
                </template>

                Unblock storage
            </AtomicButton>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div class="flex flex-col">
                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">ID:</div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedUser.id }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        Flexibee ID:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{
                            personalTeam.new_flexibee_id
                                ? 'TOP_' + personalTeam.id
                                : '-'
                        }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        Email verified:
                    </div>

                    <div
                        v-if="selectedUser.email_verified_at"
                        class="ml-1 text-sm text-gray-500"
                    >
                        {{
                            format(
                                new Date(selectedUser.email_verified_at),
                                "d. L. yyyy '('HH:mm:ss')'"
                            )
                        }}
                    </div>
                    <div v-else>
                        <div class="ml-1 text-sm text-topol-red-600">
                            Not verified
                        </div>
                    </div>
                </div>

                <div v-if="!selectedUser.email_verified_at">
                    <AtomicButton
                        size="sm"
                        color="yellow"
                        class="mt-2"
                        @click="handleSendVerificationEmail(selectedUser)"
                    >
                        Resend verification email
                    </AtomicButton>
                </div>
                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        Last login:
                    </div>

                    <div
                        v-if="selectedUser.last_login_at"
                        class="ml-1 text-sm text-gray-500"
                    >
                        {{
                            format(
                                new Date(selectedUser.last_login_at),
                                "d. L. yyyy '('HH:mm:ss')'"
                            )
                        }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        Marked for deletion at:
                    </div>

                    <div
                        v-if="selectedUser.deleted_at"
                        class="ml-1 text-sm text-gray-500"
                    >
                        {{
                            format(
                                new Date(selectedUser.deleted_at),
                                'd. L. yyyy'
                            )
                        }}
                    </div>
                </div>

                <div class="mt-1 text-sm text-gray-500">
                    <div class="font-medium text-gray-800">Subscription:</div>
                    <div v-if="selectedUser.pro_subscription">
                        PRO
                        <span>
                            ({{
                                selectedUser.pro_subscription
                                    .subscription_plan_uuid
                            }})</span
                        >

                        <span
                            v-if="
                                selectedUser.pro_subscription
                                    .recurring_payment_request
                            "
                        >
                            | recurring payment requests</span
                        >
                    </div>
                    <div v-if="selectedUser.plugin_subscription">
                        Plugin
                        <span>
                            ({{
                                selectedUser.plugin_subscription
                                    .subscription_plan_uuid
                            }})</span
                        >
                        <span
                            v-if="
                                selectedUser.plugin_subscription
                                    .recurring_payment_request
                            "
                        >
                            | recurring payment requests</span
                        >
                    </div>
                </div>

                <div class="mt-1 flex items-center">
                    <div class="text-sm font-medium text-gray-800">
                        Two-factor authentication:
                    </div>

                    <div
                        v-if="selectedUser.two_factor_enabled"
                        class="ml-1 flex items-center text-sm font-medium text-green-600"
                    >
                        Enabled
                        <span
                            class="ml-1 cursor-pointer text-xs font-medium text-topol-red-600 underline hover:opacity-90"
                            @click="
                                handleDisableTwoFactorAuthentication(
                                    selectedUser
                                )
                            "
                            >(disable)</span
                        >
                    </div>
                    <div
                        v-else
                        class="ml-1 text-sm font-medium text-topol-red-600"
                    >
                        Disabled
                    </div>
                </div>

                <div class="mt-1 flex items-center">
                    <div class="text-sm font-medium text-gray-800">
                        App Type:
                    </div>

                    <select
                        v-model="appTypeSelection"
                        class="ml-2 rounded-md border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 focus:border-topol-purple-500 focus:outline-none focus:ring-1 focus:ring-topol-purple-500"
                        @change="handleAppTypeChange"
                    >
                        <option :value="null">None</option>
                        <option value="pro">PRO</option>
                        <option value="plugin">Plugin</option>
                        <option value="both">Both</option>
                    </select>
                </div>

                <div class="mt-1 flex items-center">
                    <div class="text-sm font-medium text-gray-800">
                        Personal Team ID
                    </div>

                    <div class="ml-1 text-sm font-medium text-topol-red-600">
                        {{ personalTeam.id }}
                    </div>
                </div>

                <div class="mt-1 flex items-center">
                    <div class="text-sm font-medium text-gray-800">
                        Storage blocked at:
                    </div>

                    <div class="ml-1 text-sm font-medium text-topol-red-600">
                        {{ personalTeam.bucket_blocked_at }}
                    </div>
                </div>

                <div class="mt-4">
                    <div class="text-sm font-medium text-gray-800">Note:</div>
                    <div class="mt-1 max-w-xs text-base text-gray-500">
                        {{ selectedUser.admin_note }}
                    </div>
                    <div
                        class="mt-1 cursor-pointer text-xs font-medium text-blue-500 underline hover:opacity-90"
                        @click="editNote(selectedUser)"
                    >
                        add/edit note
                    </div>
                </div>
            </div>
            <div class="flex flex-col">
                <div class="text-gray-80 text-sm font-medium">
                    PRO trial ends:

                    <TextInput
                        v-model="proDateComputed"
                        type="datetime-local"
                        class="mt-3 w-48"
                    />
                    <AtomicButton
                        size="sm"
                        color="purple"
                        class="mt-2"
                        @click="handleExtendTrial('pro')"
                    >
                        Extend Pro Trial
                    </AtomicButton>
                </div>

                <div class="text-gray-80 mt-4 text-sm font-medium">
                    Plugin trial ends:

                    <TextInput
                        v-model="pluginDateComputed"
                        type="datetime-local"
                        class="mt-3 w-48"
                    />

                    <AtomicButton
                        size="sm"
                        color="purple"
                        class="mt-2"
                        @click="handleExtendTrial('plugin')"
                    >
                        Extend Plugin Trial
                    </AtomicButton>
                </div>
            </div>
        </div>
    </div>

    <MakePaymentRequestModal
        v-if="showPaymentRequestModal"
        :team="personalTeam"
        @close="showPaymentRequestModal = false"
    ></MakePaymentRequestModal>

    <ManageRecurringPaymentRequestsModal
        v-if="
            showManageRecurringPaymentRequestsModal &&
            (selectedUser.pro_subscription || selectedUser.plugin_subscription)
        "
        :pro="selectedUser.pro_subscription"
        :plugin="selectedUser.plugin_subscription"
        @close="showManageRecurringPaymentRequestsModal = false"
        @enable="enablePaymentRequestRecurring"
        @disable="disablePaymentRequestRecurring"
    ></ManageRecurringPaymentRequestsModal>
</template>

<script lang="ts" setup>
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';
import { format } from 'date-fns';
import {
    TrashIcon,
    EyeIcon,
    AtSymbolIcon,
    DocumentTextIcon,
    FireIcon,
    SparklesIcon,
    BanknotesIcon,
} from '@heroicons/vue/24/outline';

import { route } from 'ziggy-js';
import { router } from '@inertiajs/vue3';
import IKioskUser from '@/types/Members/IKioskUser';
import useConfirm from '@/composables/useConfirm';
import { create } from '@/composables/useNotifications';
import axios from 'axios';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import { ref, computed, watch } from 'vue';
import MakePaymentRequestModal from '@/Pages/Kiosk/PaymentRequests/components/MakePaymentRequestModal.vue';
import CloseLogo from '@/Pages/Integrations/components/Close/CloseLogo.vue';
import { useReCaptcha } from 'vue-recaptcha-v3';

const props = defineProps<{
    selectedUser: IKioskUser;
    personalTeam: ICurrentTeam;
}>();

const { show } = useConfirm();
const reCaptcha = useReCaptcha();

const proDate = ref('');
const pluginDate = ref('');

const showPaymentRequestModal = ref(false);
const showManageRecurringPaymentRequestsModal = ref(false);

const createLeadLoading = ref(false);

const appTypeSelection = ref<string | null>(props.selectedUser.app_type ?? null);

// Watch for changes in selectedUser to update appTypeSelection
watch(
    () => props.selectedUser.app_type,
    (newValue) => {
        appTypeSelection.value = newValue ?? null;
    },
    { immediate: true }
);

const handleDeleteUser = async (selectedUser: IKioskUser) => {
    const confirmed = await show({
        header: `Delete user ${selectedUser.name}`,
        message: `Are you sure you want to delete user ${selectedUser.name}?`,
        type: 'danger',
        confirm: 'Delete user',
    });
    if (confirmed) {
        await deleteUser(selectedUser);
    }
};

const handleResetPassword = async (selectedUser: IKioskUser) => {
    const confirmed = await show({
        header: `Reset password for user ${selectedUser.name}`,
        message: `Are you sure you want to sent reset password link for user ${selectedUser.name}?`,
        type: 'danger',
        confirm: 'Reset password',
    });
    if (confirmed) {
        await resetPassword(selectedUser);
    }
};

const handleSendVerificationEmail = async (selectedUser: IKioskUser) => {
    const confirmed = await show({
        header: 'Resend email verification',
        message: `Are you sure you want to resend verification email to user ${selectedUser.name}?`,
        type: 'danger',
        confirm: 'Send',
    });
    if (confirmed) {
        await sendVerificationEmail(selectedUser);
    }
};

const handleImpersonate = (selectedUser: IKioskUser) => {
    router.get(route('kiosk.users.impersonate', { user: selectedUser.id }));
};

const deleteUser = async (selectedUser: IKioskUser) => {
    try {
        const res = await axios.delete(
            route('kiosk.users.delete', { user: selectedUser.id })
        );
        if (res.data.success) {
            create({
                title: `User deleted`,
                type: 'success',
            });
            router.get(route('kiosk.users.index'));
        } else {
            create({
                title: 'User not deleted',
                text: `There was an error deleting user.`,
                type: 'error',
            });
        }
    } catch (err) {
        create({
            title: 'User not deleted',
            text: `There was an error deleting user.`,
            type: 'error',
        });
    }
};

const resetPassword = async (selectedUser: IKioskUser) => {
    try {
        await reCaptcha?.recaptchaLoaded();
        const token = await reCaptcha?.executeRecaptcha('forgotPassword');

        if (token === undefined) {
            throw new Error();
        }

        const res = await axios.post(
            route('kiosk.users.reset-password', { user: selectedUser.id }),
            {
                recaptcha_token: token,
            }
        );
        if (res.data.success) {
            create({
                title: `Reset link has been sent`,
                type: 'success',
            });
        } else {
            create({
                title: 'Reset link has not been sent',
                text: `There was an error sending reset password link user.`,
                type: 'error',
            });
        }
    } catch (err) {
        create({
            title: 'Reset link has not been sent',
            text: `There was an error sending reset password link user.`,
            type: 'error',
        });
    }
};

const sendVerificationEmail = async (selectedUser: IKioskUser) => {
    try {
        const res = await axios.post(
            route('kiosk.users.verification-email', { user: selectedUser.id })
        );
        if (res.data.success) {
            create({
                title: 'Verification email sent',
                type: 'success',
            });
        } else {
            create({
                title: 'Verification email not send',
                type: 'error',
            });
        }
    } catch (err) {
        create({
            title: 'Verification email not send',
            type: 'error',
        });
    }
};

const handleDisableTwoFactorAuthentication = async (
    selectedUser: IKioskUser
) => {
    const confirmed = await show({
        header: `Disable 2FA for user ${selectedUser.name}`,
        message: `Are you sure you want to disable two-factor authentication for user ${selectedUser.name}?`,
        type: 'danger',
        confirm: 'Disable',
    });
    if (confirmed) {
        await disableTwoFactorAuthentication(selectedUser);
    }
};

const disableTwoFactorAuthentication = (selectedUser: IKioskUser) => {
    router.put(
        route('kiosk.users.2fa.disable', { user: selectedUser.id }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: `2FA for ${selectedUser.name} disabled`,
                    type: 'success',
                });
            },
            onError: () => {
                create({
                    title: `2FA for ${selectedUser.name} not disabled`,
                    text: `There was an error disabling 2FA.`,
                    type: 'error',
                });
            },
        }
    );
};

const editNote = async (selectedUser: IKioskUser) => {
    const note = await show({
        header: 'Add / edit note',
        hasInput: true,
        confirm: 'Update',
        inputData: {
            label: 'Enter note',
            defaultValue: selectedUser.admin_note,
        },
    });

    if (note) {
        router.put(
            route('kiosk.users.update', { user: selectedUser.id }),
            {
                note: typeof note === 'string' ? note : null,
            },
            {
                preserveScroll: true,
            }
        );
    }
};

const handleExtendTrial = async (type: 'pro' | 'plugin') => {
    const confirmed = await show({
        header: `Extend ${type} trial`,
        message: `Are you sure you want to update trial date of user?`,
        type: 'info',
        confirm: 'Update',
    });

    if (confirmed) {
        router.put(
            route('kiosk.users.trial-extend.update', {
                user: props.selectedUser.id,
            }),
            {
                type: type,
                date: type === 'pro' ? proDate.value : pluginDate.value,
            },
            {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    create({
                        title: `Trial extended`,
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Trial not deleted',
                        text: `There was an error extending trial.`,
                        type: 'error',
                    });
                },
                onFinish: () => {
                    proDate.value = '';
                    pluginDate.value = '';
                },
            }
        );
    }
};

const proDateComputed = computed({
    get: function () {
        return props.selectedUser.pro_trial_ends_at
            ? new Date(props.selectedUser.pro_trial_ends_at)
                  .toISOString()
                  .substring(0, 19)
            : '';
    },
    set: function (newValue: string) {
        proDate.value = newValue;
    },
});

const pluginDateComputed = computed({
    get: function () {
        return props.selectedUser.plugin_trial_ends_at
            ? new Date(props.selectedUser.plugin_trial_ends_at)
                  .toISOString()
                  .substring(0, 19)
            : '';
    },
    set: function (newValue: string) {
        pluginDate.value = newValue;
    },
});

const createFlexibeeId = async () => {
    const confirmed = await show({
        header: 'Create Flexibee ID',
        message: `Are you sure you want to create Flexibee ID for this user?`,
        type: 'info',
        confirm: 'Create',
    });
    if (confirmed) {
        const response = await axios.post(
            route('kiosk.teams.flexibee.store', {
                team: props.personalTeam.id,
            })
        );

        if (response.status === 200) {
            create({
                title: `Flexibee ID created`,
                type: 'success',
            });
            router.reload();
        } else {
            create({
                title: 'Flexibee ID not created',
                text: `There was an error creating Flexibee ID.`,
                type: 'error',
            });
        }
    }
};

const makeBucketPrivate = async (user: IKioskUser) => {
    const confirmed = await show({
        header: 'Block storage for selected User',
        message: `Are you sure you want to block storage for this user?`,
        type: 'danger',
        confirm: 'Block',
    });
    if (confirmed) {
        router.post(
            route('kiosk.users.make-bucket-private', { user: user.id }),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Storage has been blocked',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Storage has not been blocked',
                        type: 'error',
                    });
                },
            }
        );
    }
};

const makeBucketPublic = async (user: IKioskUser) => {
    const confirmed = await show({
        header: 'Unblock storage for selected User',
        message: `Are you sure you want to unblock storage for this user?`,
        type: 'danger',
        confirm: 'Unblock',
    });
    if (confirmed) {
        router.post(
            route('kiosk.users.make-bucket-public', { user: user.id }),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Storage has been unblocked',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Storage has not been unblocked',
                        type: 'error',
                    });
                },
            }
        );
    }
};

const enablePaymentRequestRecurring = async (subscriptionId: number) => {
    const confirmed = await show({
        header: 'Enable recurring payment requests',
        message:
            'Are you sure you want to enable recurring payment requests for this subscription?',
        type: 'danger',
        confirm: 'Enable',
    });

    if (confirmed) {
        showManageRecurringPaymentRequestsModal.value = false;

        router.post(
            route(
                'kiosk.subscriptions.enable-recurring-payment-requests.store',
                {
                    subscription: subscriptionId,
                }
            ),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Payment request recurring enabled',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Payment request recurring not enabled',
                        text: 'There was an error enabling payment request recurring.',
                        type: 'error',
                    });
                },
            }
        );
    }
};

const disablePaymentRequestRecurring = async (subscriptionId: number) => {
    const confirmed = await show({
        header: 'Disable recurring payment requests',
        message:
            'Are you sure you want to disable recurring payment requests for this subscription? It will also CANCEL it.',
        type: 'danger',
        confirm: 'Disable',
    });

    if (confirmed) {
        showManageRecurringPaymentRequestsModal.value = false;

        router.post(
            route(
                'kiosk.subscriptions.disable-recurring-payment-requests.store',
                {
                    subscription: subscriptionId,
                }
            ),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Payment request recurring disabled',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Payment request recurring not disabled',
                        text: 'There was an error disabling payment request recurring.',
                        type: 'error',
                    });
                },
            }
        );
    }
};

const handleAppTypeChange = async () => {
    const confirmed = await show({
        header: 'Change App Type',
        message: `Are you sure you want to change the app type to "${appTypeSelection.value || 'None'}"?`,
        type: 'warning',
        confirm: 'Change',
    });

    if (confirmed) {
        router.put(
            route('kiosk.users.app-type.update', {
                user: props.selectedUser.id,
            }),
            {
                app_type: appTypeSelection.value,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'App type updated',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'App type not updated',
                        text: 'There was an error updating the app type.',
                        type: 'error',
                    });
                    // Reset to original value on error
                    appTypeSelection.value = props.selectedUser.app_type;
                },
            }
        );
    } else {
        // Reset to original value if cancelled
        appTypeSelection.value = props.selectedUser.app_type;
    }
};
</script>
